import type { AssistLevel } from '../tunables.js';

/**
 * The save schema, and a deliberately paranoid migration.
 *
 * Losing her drawings is the one unrecoverable bug in this project - there is no way to apologise
 * to a three-year-old for deleting her cars. So: the version lives in the file from day one,
 * `migrate` never throws and never discards a car it can partially understand, and the storage
 * layer keeps the previous save as a rolling backup (see src/save/db.ts).
 *
 * Note there are no Blobs in here. Baked PNG thumbnails live in their own IndexedDB store keyed by
 * car id, which keeps this module pure data and therefore trivially testable.
 */

export const SCHEMA_VERSION = 2;

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** A single freehand crayon stroke, stored as vector points rather than pixels. */
export interface Stroke {
  readonly points: readonly Point[];
  readonly colour: string;
  readonly width: number;
  /** Which template anchor this stroke was drawn for, if any (e.g. 'anchor-wheel-front'). */
  readonly anchor: string | null;
  /**
   * The panel she started drawing in, and the only one this mark may show inside.
   *
   * A mark is clipped to its own region rather than to the union of them all, so a scribble that runs
   * off the door stops at the door's edge instead of carrying on across the whole side of the truck.
   * That is what makes freehand read as colouring-in rather than as drawing over the picture.
   *
   * `null` means a mark saved before this existed. Those stay clipped to the union - the old
   * behaviour - because re-clipping a drawing she already has would silently cut pieces out of it.
   */
  readonly regionId: string | null;
}

/** A sticker she has stuck on the car, positioned in template coordinates. */
export interface PlacedSticker {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Size as a fraction of the template's width, so a sticker scales with the vehicle. */
  readonly scale: number;
  /** Radians. A little random tilt per sticker, so a row of them does not look printed. */
  readonly rotation: number;
}

export interface SavedCar {
  readonly id: string;
  readonly templateId: string;
  /** SVG region id -> CSS colour, e.g. { 'region-body': '#e33' }. The entire colouring. */
  readonly regionColours: Readonly<Record<string, string>>;
  readonly strokes: readonly Stroke[];
  readonly stickers: readonly PlacedSticker[];
  /** ISO 8601. Passed in by the caller - core never reads the clock. */
  readonly createdAt: string;
  readonly bakeWidth: number;
}

export interface Settings {
  readonly assistLevel: AssistLevel;
  readonly musicVolume: number;
  readonly sfxVolume: number;
  readonly freehandMode: boolean;
  /** Minutes before the "the pups are sleeping" screen, or null for no limit. */
  readonly sessionLimitMinutes: number | null;
}

export interface MissionRecord {
  readonly missionId: string;
  readonly carId: string;
  readonly at: string;
  readonly pointsAwarded: number;
}

export interface SaveData {
  readonly schemaVersion: number;
  readonly pawPoints: number;
  /**
   * The prizes she has been given, by unlock id.
   *
   * Ids from a build this one does not know are kept rather than dropped, which is what makes the v1
   * hat ladder survivable: `hat-cap` means nothing to the current prize table, `unlockById` shrugs at it
   * and it can never be re-granted. Her *points* are what carry a prize forward - see `migrate`.
   */
  readonly unlocks: readonly string[];
  readonly settings: Settings;
  readonly garage: readonly SavedCar[];
  readonly missionHistory: readonly MissionRecord[];
  /**
   * How many drives she has been *sent on*. The world's seed, and nothing else.
   *
   * Separate from `missionHistory.length` on purpose. The route used to be seeded from the history, which
   * meant the map only changed when a mission was carried all the way through to the reward jar - so
   * wandering off halfway, or a reload, handed her a byte-identical world next time. At three, "off to
   * somewhere new" is most of the appeal, and abandoning a drive is the *normal* case.
   *
   * A counter rather than `Math.random`: it persists, so a route she is halfway through survives a reload,
   * and a route a tester reports can be reached again by setting this number.
   */
  readonly routeCounter: number;
}

export const DEFAULT_SETTINGS: Settings = {
  assistLevel: 3,
  musicVolume: 0.6,
  sfxVolume: 0.9,
  freehandMode: false,
  sessionLimitMinutes: null,
};

export function emptySave(): SaveData {
  return {
    schemaVersion: SCHEMA_VERSION,
    pawPoints: 0,
    unlocks: [],
    settings: DEFAULT_SETTINGS,
    garage: [],
    missionHistory: [],
    routeCounter: 0,
  };
}

/**
 * Coerce whatever came out of storage into a valid SaveData.
 *
 * Never throws. Anything unrecognisable falls back to a default *for that field only* - a corrupt
 * settings object must not cost her the garage.
 */
export function migrate(raw: unknown): SaveData {
  if (!isRecord(raw)) return emptySave();

  const base = emptySave();

  const missionHistory = asArray(raw['missionHistory']).flatMap((entry) => {
    const record = migrateMissionRecord(entry);
    return record === null ? [] : [record];
  });

  /*
   * v0/v1 -> v2, and the first version step that actually drops something.
   *
   * v2 is the rewards rework: `equippedHat` is gone, because the prizes are cars and stickers and neither
   * is worn. **There is still no branch on `schemaVersion`, and this is the version step that shows why
   * there does not need to be one:** a field-by-field coercion that never asks for a field cannot carry it,
   * and every field v2 does want means in a v1 file exactly what it means in a v2 one. A branch would be
   * written the day a field changes *meaning* rather than merely appearing or leaving - and the version is
   * in every file this build has ever written, so that day is still cheap.
   *
   * **Her hats are not converted into anything, and she loses nothing.** A v1 save carries
   * `unlocks: ['hat-cap', ...]` and those ids mean nothing to the new table - but `pawPoints` is untouched,
   * and the ladder is claimed from points (`claimUnlocks`), so every prize she had paid for arrives on her
   * next reward screen, all at once, as prizes she can actually see. Mapping hat ids onto sticker sets
   * would have been guesswork that could only ever hand her *fewer* things than her points already buy.
   */
  const migrated: SaveData = {
    schemaVersion: SCHEMA_VERSION,
    pawPoints: Math.max(0, Math.floor(asFiniteNumber(raw['pawPoints'], base.pawPoints))),
    unlocks: asStringArray(raw['unlocks']),
    settings: migrateSettings(raw['settings']),
    garage: asArray(raw['garage']).flatMap((entry) => {
      const car = migrateCar(entry);
      return car === null ? [] : [car];
    }),
    missionHistory,
    /*
     * A save written before this field existed gets the count of missions she has finished, not zero -
     * that is the number the seed used to come from, so an existing player carries on from where she is
     * rather than being sent back to the very first map.
     */
    routeCounter: Math.max(
      0,
      Math.floor(asFiniteNumber(raw['routeCounter'], missionHistory.length)),
    ),
  };

  return migrated;
}

function migrateSettings(raw: unknown): Settings {
  if (!isRecord(raw)) return DEFAULT_SETTINGS;
  return {
    assistLevel: asAssistLevel(raw['assistLevel']),
    musicVolume: clamp01(asFiniteNumber(raw['musicVolume'], DEFAULT_SETTINGS.musicVolume)),
    sfxVolume: clamp01(asFiniteNumber(raw['sfxVolume'], DEFAULT_SETTINGS.sfxVolume)),
    freehandMode: raw['freehandMode'] === true,
    sessionLimitMinutes: isFiniteNumber(raw['sessionLimitMinutes'])
      ? Math.max(1, Math.floor(raw['sessionLimitMinutes']))
      : null,
  };
}

/**
 * A car survives migration if it has an id and a template. Everything else is recoverable:
 * a car with no colours is just an uncoloured car, which is far better than a missing one.
 */
function migrateCar(raw: unknown): SavedCar | null {
  if (!isRecord(raw)) return null;
  const id = asNonEmptyString(raw['id']);
  const templateId = asNonEmptyString(raw['templateId']);
  if (id === null || templateId === null) return null;

  return {
    id,
    templateId,
    regionColours: asStringRecord(raw['regionColours']),
    strokes: asArray(raw['strokes']).flatMap((s) => {
      const stroke = migrateStroke(s);
      return stroke === null ? [] : [stroke];
    }),
    stickers: asArray(raw['stickers']).flatMap((entry) => {
      const sticker = migrateSticker(entry);
      return sticker === null ? [] : [sticker];
    }),
    createdAt: asNonEmptyString(raw['createdAt']) ?? '1970-01-01T00:00:00.000Z',
    bakeWidth: Math.max(64, Math.floor(asFiniteNumber(raw['bakeWidth'], 1024))),
  };
}

function migrateStroke(raw: unknown): Stroke | null {
  if (!isRecord(raw)) return null;
  const points = asArray(raw['points']).flatMap((p) => {
    if (!isRecord(p)) return [];
    const x = numberOrNull(p['x']);
    const y = numberOrNull(p['y']);
    return x === null || y === null ? [] : [{ x, y }];
  });
  if (points.length === 0) return null;

  return {
    points,
    colour: asNonEmptyString(raw['colour']) ?? '#333333',
    width: Math.max(1, asFiniteNumber(raw['width'], 22)),
    anchor: asNonEmptyString(raw['anchor']),
    // Absent in saves written before marks were confined to one panel; null keeps those clipped to
    // the union, so an existing drawing is never re-cropped underneath her.
    regionId: asNonEmptyString(raw['regionId']),
  };
}

/**
 * A sticker survives if it has an id and a position. Size and tilt are cosmetic and get sane defaults -
 * a sticker in slightly the wrong place is still her sticker.
 */
function migrateSticker(raw: unknown): PlacedSticker | null {
  if (!isRecord(raw)) return null;
  const id = asNonEmptyString(raw['id']);
  const x = numberOrNull(raw['x']);
  const y = numberOrNull(raw['y']);
  if (id === null || x === null || y === null) return null;

  return {
    id,
    x,
    y,
    scale: clampRange(asFiniteNumber(raw['scale'], 0.14), 0.02, 1),
    rotation: Number.isFinite(raw['rotation']) ? (raw['rotation'] as number) : 0,
  };
}

function migrateMissionRecord(raw: unknown): MissionRecord | null {
  if (!isRecord(raw)) return null;
  const missionId = asNonEmptyString(raw['missionId']);
  if (missionId === null) return null;
  return {
    missionId,
    carId: asNonEmptyString(raw['carId']) ?? '',
    at: asNonEmptyString(raw['at']) ?? '1970-01-01T00:00:00.000Z',
    pointsAwarded: Math.max(0, Math.floor(asFiniteNumber(raw['pointsAwarded'], 0))),
  };
}

/* -------------------------------------------------------------------------- */
/* Coercion helpers                                                            */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function numberOrNull(value: unknown): number | null {
  return isFiniteNumber(value) ? value : null;
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return isFiniteNumber(value) ? value : fallback;
}

function asNonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asArray(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function asStringArray(value: unknown): readonly string[] {
  return asArray(value).filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function asStringRecord(value: unknown): Readonly<Record<string, string>> {
  if (!isRecord(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, v] of Object.entries(value)) {
    if (typeof v === 'string' && v.length > 0) out[key] = v;
  }
  return out;
}

function asAssistLevel(value: unknown): AssistLevel {
  return value === 0 || value === 1 || value === 2 || value === 3
    ? value
    : DEFAULT_SETTINGS.assistLevel;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampRange(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}
